/* ============================================================
   ModalTransicionEtapa
   - tipo = 'contacto'  → Precaptación → resultado a elegir:
       1. Registrar contacto  → avanza a Contactados
       2. Agendar cita        → avanza a Visita Concertada
       3. Mandar a Frías      → motivo + tarea de recontacto opcional
   - tipo = 'cita'      → Contactados → Visita Concertada (sin opciones)
   ============================================================ */

import { useState, useEffect } from 'react';
import { useTheme } from './ThemeProvider';
import CitaSelectorHora from './cita/CitaSelectorHora';

const CANALES = [
  { id: 'whatsapp',   label: 'WhatsApp',   emoji: '💬' },
  { id: 'llamada',    label: 'Llamada',    emoji: '📞' },
  { id: 'email',      label: 'Email',      emoji: '✉️' },
  { id: 'presencial', label: 'En persona', emoji: '🤝' },
];

/** Duraciones predeterminadas (h) + input manual. */
const DURACIONES_CAPTACION = [
  { h: 1, label: '1h' },
  { h: 1.5, label: '1,5h' },
  { h: 2, label: '2h' },
];

const hoy = () => new Date().toISOString().split('T')[0];
const ahora = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function mismaDuracion(a, b) {
  return Math.abs(parseFloat(a) - parseFloat(b)) < 0.001;
}

export default function ModalTransicionEtapa({ isOpen, tipo, captacion, onClose, onConfirmar }) {
  const { theme } = useTheme();
  const c = theme.colors;

  const esCita = tipo === 'cita';

  // Cuando es 'contacto', el agente elige la opción primero
  // null = elige, 'contacto' | 'cita' | 'fria' | 'eliminado'
  const [opcion, setOpcion] = useState(esCita ? 'cita' : null);

  const [form, setForm] = useState({
    fecha: hoy(), hora: ahora(), canal: 'whatsapp', contenido: '', duracion: '1',
    motivo: '', recontactar: false, rec_fecha: hoy(), rec_hora: '', rec_desc: '',
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (isOpen) {
      setOpcion(esCita ? 'cita' : null);
      setForm({
        fecha: hoy(), hora: ahora(), canal: 'whatsapp', contenido: '', duracion: '1',
        motivo: '', recontactar: false, rec_fecha: hoy(), rec_hora: '', rec_desc: '',
      });
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const nombreCompleto = [captacion?.nombre, captacion?.apellido].filter(Boolean).join(' ') || '—';

  const inp = {
    width: '100%', padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${c.border}`, background: c.bgInput,
    color: c.textPrimary, fontSize: 13, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const lbl = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    display: 'block', marginBottom: 5,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  const confirmar = async () => {
    if (!opcion) return;
    const sinFecha = opcion !== 'fria' && opcion !== 'eliminado';
    if (sinFecha && !form.fecha) { setError('La fecha es obligatoria'); return; }
    if ((opcion === 'fria' || opcion === 'eliminado') && !form.motivo.trim()) { setError('Indica el motivo'); return; }
    setGuardando(true); setError('');
    try {
      const fechaAccion = sinFecha ? new Date(
        form.hora ? `${form.fecha}T${form.hora}:00` : `${form.fecha}T00:00:00`
      ).toISOString() : null;

      if (opcion === 'eliminado') {
        await onConfirmar({
          resultado: 'eliminado',
          tipo:      'sistema',
          motivo:    form.motivo.trim(),
        });
      } else if (opcion === 'fria') {
        let recontactar_fecha = null;
        if (form.recontactar && form.rec_fecha) {
          recontactar_fecha = new Date(
            form.rec_hora ? `${form.rec_fecha}T${form.rec_hora}:00` : `${form.rec_fecha}T09:00:00`
          ).toISOString();
        }
        await onConfirmar({
          resultado:         'fria',
          tipo:              'sistema',
          motivo:            form.motivo.trim(),
          recontactar_fecha,
          rec_desc:          form.rec_desc.trim() || null,
        });
      } else if (opcion === 'cita') {
        await onConfirmar({
          resultado:     'cita',
          tipo:          'cita',
          canal:         form.canal,
          contenido:     form.contenido.trim() || null,
          duracion_horas: parseFloat(form.duracion) || 1,
          fecha_accion:  fechaAccion,
        });
      } else {
        await onConfirmar({
          resultado:    'contacto',
          tipo:         'contacto',
          canal:        form.canal,
          contenido:    form.contenido.trim() || null,
          fecha_accion: fechaAccion,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al guardar');
      setGuardando(false);
    }
  };

  // ── Colores según opción activa ───────────────────────────────────────────
  const colorMap = {
    contacto:  { bar: 'linear-gradient(90deg,#22c55e,#3b82f6)', btn: 'linear-gradient(135deg,#22c55e,#3b82f6)', icon: '💬', iconBg: 'rgba(34,197,94,0.12)' },
    cita:      { bar: 'linear-gradient(90deg,#f59e0b,#ef4444)', btn: 'linear-gradient(135deg,#f59e0b,#ef4444)', icon: '📅', iconBg: 'rgba(245,158,11,0.15)' },
    fria:      { bar: 'linear-gradient(90deg,#64748b,#3b82f6)', btn: 'linear-gradient(135deg,#64748b,#3b82f6)', icon: '❄️', iconBg: 'rgba(100,116,139,0.15)' },
    eliminado: { bar: 'linear-gradient(90deg,#ef4444,#b91c1c)', btn: 'linear-gradient(135deg,#ef4444,#b91c1c)', icon: '🗑️', iconBg: 'rgba(239,68,68,0.12)' },
  };
  const activeColor = colorMap[opcion] || colorMap.contacto;

  const tituloModal = opcion === 'cita'      ? 'Agendar captación'
                    : opcion === 'fria'      ? 'Mandar a Frías'
                    : opcion === 'contacto'  ? 'Registrar contacto'
                    : opcion === 'eliminado' ? 'Mandar a Eliminados'
                    : '¿Cuál fue el resultado?';

  // ── Canal selector ────────────────────────────────────────────────────────
  const CanalSelector = () => (
    <div style={{ marginBottom: 14 }}>
      <label style={lbl}>Medio</label>
      <div style={{ display: 'flex', gap: 6 }}>
        {CANALES.map(ch => (
          <button key={ch.id}
            onClick={() => setForm(p => ({ ...p, canal: ch.id }))}
            style={{
              flex: 1, padding: '7px 4px', borderRadius: 8,
              border: `1.5px solid ${form.canal === ch.id ? '#3b82f6' : c.border}`,
              background: form.canal === ch.id ? 'rgba(59,130,246,0.12)' : c.bgInput,
              color: form.canal === ch.id ? '#60a5fa' : c.textMuted,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 10, fontWeight: 600,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              transition: 'all 0.15s',
            }}>
            <span style={{ fontSize: 16 }}>{ch.emoji}</span>{ch.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1600,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: theme.name === 'dark' ? c.bgCard : '#c8c8be',
        border: `1px solid ${theme.name === 'dark' ? c.border : '#a8a8a0'}`,
        borderRadius: 16, width: 460, maxWidth: '95vw',
        boxShadow: theme.name === 'dark' ? '0 24px 64px rgba(0,0,0,0.45)' : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        overflow: 'hidden',
      }}>
        {/* Barra de color */}
        <div style={{ height: 3, background: activeColor.bar, transition: 'background 0.3s' }} />

        {/* Header */}
        <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: theme.name === 'dark' ? 'transparent' : '#e0e0d8', borderBottom: `1px solid ${theme.name === 'dark' ? c.border : '#a8a8a0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, fontSize: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: activeColor.iconBg, transition: 'background 0.3s',
            }}>
              {activeColor.icon}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary }}>{tituloModal}</div>
              <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>{nombreCompleto}</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.border}`,
            background: c.bgInput, cursor: 'pointer', color: c.textMuted,
            fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        <div style={{ padding: theme.name === 'dark' ? '16px 24px 20px' : '12px 12px 16px' }}>

          {/* ── PASO 1: Elegir opción (solo cuando tipo='contacto' y no se ha elegido) ── */}
          {!opcion && (
            <>
              <p style={{ fontSize: 12, color: c.textMuted, margin: '0 0 16px' }}>
                Indica qué ocurrió al contactar con el propietario
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                {/* Agendar cita */}
                <button onClick={() => setOpcion('cita')} style={{
                  padding: '14px 18px', borderRadius: 12, border: `1.5px solid rgba(245,158,11,0.4)`,
                  background: 'rgba(245,158,11,0.08)', cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(245,158,11,0.4)'}
                >
                  <span style={{ fontSize: 24 }}>📅</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>Agendar cita</div>
                    <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
                      Propietario interesado — fijamos fecha y hora de captación
                    </div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: c.textMuted, fontSize: 16 }}>›</span>
                </button>

                {/* Registrar contacto */}
                <button onClick={() => setOpcion('contacto')} style={{
                  padding: '14px 18px', borderRadius: 12, border: `1.5px solid rgba(34,197,94,0.35)`,
                  background: 'rgba(34,197,94,0.07)', cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#22c55e'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(34,197,94,0.35)'}
                >
                  <span style={{ fontSize: 24 }}>💬</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>Registrar contacto</div>
                    <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
                      Hablamos pero no hay fecha todavía — avanzar a Contactados
                    </div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: c.textMuted, fontSize: 16 }}>›</span>
                </button>

                {/* Mandar a Frías */}
                <button onClick={() => setOpcion('fria')} style={{
                  padding: '14px 18px', borderRadius: 12, border: `1.5px solid rgba(100,116,139,0.35)`,
                  background: 'rgba(100,116,139,0.07)', cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
                  width: '100%',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#64748b'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(100,116,139,0.35)'}
                >
                  <span style={{ fontSize: 24 }}>❄️</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>Mandar a Frías</div>
                    <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
                      No interesado por ahora — con opción de crear tarea de recontacto
                    </div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: c.textMuted, fontSize: 16 }}>›</span>
                </button>

                {/* Mandar a Eliminados */}
                <button onClick={() => setOpcion('eliminado')} style={{
                  padding: '14px 18px', borderRadius: 12, border: `1.5px solid rgba(239,68,68,0.35)`,
                  background: 'rgba(239,68,68,0.06)', cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', width: '100%',
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#ef4444'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(239,68,68,0.35)'}
                >
                  <span style={{ fontSize: 24 }}>🗑️</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>Mandar a Eliminados</div>
                    <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
                      Descartado definitivamente — indica el motivo
                    </div>
                  </div>
                  <span style={{ marginLeft: 'auto', color: c.textMuted, fontSize: 16 }}>›</span>
                </button>

              </div>
            </>
          )}

          {/* ── PASO 2: Formulario según opción ── */}
          {opcion && (
            <>
              {/* Botón volver (solo en tipo='contacto') */}
              {tipo === 'contacto' && !esCita && (
                <button onClick={() => { setOpcion(null); setError(''); }}
                  style={{ marginBottom: 14, background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                  ‹ Volver
                </button>
              )}

              {/* ── Formulario: Registrar contacto ── */}
              {opcion === 'contacto' && (
                <>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={lbl}>Fecha del contacto</label>
                      <input type="date" value={form.fecha}
                        onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} style={inp} />
                    </div>
                    <div style={{ flex: '0 0 112px' }}>
                      <label style={lbl}>Hora</label>
                      <CitaSelectorHora
                        value={form.hora || '10:00'}
                        onChange={(v) => setForm((p) => ({ ...p, hora: v }))}
                        c={c}
                        isDark={theme.name === 'dark'}
                        accentColor="#3b82f6"
                        accentBg="rgba(59,130,246,0.15)"
                        chipText={theme.name === 'dark' ? '#93c5fd' : '#1d4ed8'}
                        borderColor={c.border}
                        align="right"
                        zIndex={1700}
                      />
                    </div>
                  </div>
                  <CanalSelector />
                  <div style={{ marginBottom: 18 }}>
                    <label style={lbl}>Nota (opcional)</label>
                    <textarea value={form.contenido}
                      onChange={e => setForm(p => ({ ...p, contenido: e.target.value }))}
                      placeholder="¿Cómo fue el contacto?"
                      rows={3} style={{ ...inp, resize: 'none' }} />
                  </div>
                </>
              )}

              {/* ── Formulario: Agendar cita ── */}
              {opcion === 'cita' && (
                <>
                  <p style={{ fontSize: 12, color: c.textMuted, margin: '0 0 16px' }}>
                    Indica la fecha y hora de la captación acordada con el propietario
                  </p>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <label style={lbl}>Fecha de la captación</label>
                      <input type="date" value={form.fecha}
                        onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} style={inp} />
                    </div>
                    <div style={{ flex: '0 0 112px' }}>
                      <label style={lbl}>Hora</label>
                      <CitaSelectorHora
                        value={form.hora}
                        onChange={(v) => setForm((p) => ({ ...p, hora: v }))}
                        c={c}
                        isDark={theme.name === 'dark'}
                        accentColor="#f59e0b"
                        accentBg="rgba(245,158,11,0.15)"
                        chipText="#fbbf24"
                        borderColor={c.border}
                        align="right"
                        zIndex={1700}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={lbl}>Duración</label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {DURACIONES_CAPTACION.map((d) => {
                        const activo = mismaDuracion(form.duracion, d.h);
                        return (
                          <button
                            key={d.label}
                            type="button"
                            onClick={() => setForm((p) => ({ ...p, duracion: String(d.h) }))}
                            style={{
                              padding: '7px 12px',
                              borderRadius: 8,
                              border: `1.5px solid ${activo ? '#f59e0b' : c.border}`,
                              background: activo ? 'rgba(245,158,11,0.15)' : c.bgInput,
                              color: activo ? '#fbbf24' : c.textMuted,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              transition: 'all 0.15s',
                            }}
                          >
                            {d.label}
                          </button>
                        );
                      })}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 2 }}>
                        <input
                          type="number"
                          min="0.5"
                          max="8"
                          step="0.5"
                          value={form.duracion}
                          onChange={(e) => setForm((p) => ({ ...p, duracion: e.target.value }))}
                          style={{ ...inp, width: 72, padding: '7px 8px' }}
                          title="Duración en horas (manual)"
                        />
                        <span style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>h</span>
                      </div>
                    </div>
                  </div>
                  <CanalSelector />
                  <div style={{ marginBottom: 18 }}>
                    <label style={lbl}>Nota</label>
                    <textarea value={form.contenido}
                      onChange={e => setForm(p => ({ ...p, contenido: e.target.value }))}
                      placeholder="Detalles de la visita acordada..."
                      rows={3} style={{ ...inp, resize: 'none' }} />
                  </div>
                </>
              )}

              {/* ── Formulario: Mandar a Frías ── */}
              {opcion === 'fria' && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={lbl}>Motivo <span style={{ color: '#ef4444' }}>*</span></label>
                    <textarea value={form.motivo}
                      onChange={e => setForm(p => ({ ...p, motivo: e.target.value }))}
                      placeholder="¿Por qué se envía a Frías? (no interesado, precio alto, etc.)"
                      rows={3} style={{ ...inp, resize: 'none' }} />
                  </div>

                  {/* Toggle recontacto */}
                  <div style={{
                    padding: '12px 14px', borderRadius: 10,
                    border: `1px solid ${form.recontactar ? 'rgba(59,130,246,0.4)' : c.border}`,
                    background: form.recontactar ? 'rgba(59,130,246,0.06)' : c.bgInput,
                    marginBottom: 4, transition: 'all 0.2s',
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.recontactar}
                        onChange={e => setForm(p => ({ ...p, recontactar: e.target.checked }))}
                        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#3b82f6' }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary }}>
                          Crear tarea de recontacto
                        </div>
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 1 }}>
                          Programa cuándo volver a contactar con este propietario
                        </div>
                      </div>
                    </label>

                    {form.recontactar && (
                      <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Fecha</label>
                          <input type="date" value={form.rec_fecha}
                            onChange={e => setForm(p => ({ ...p, rec_fecha: e.target.value }))} style={inp} />
                        </div>
                        <div style={{ flex: '0 0 112px' }}>
                          <label style={lbl}>Hora</label>
                          <CitaSelectorHora
                            value={form.rec_hora || '10:00'}
                            onChange={(v) => setForm((p) => ({ ...p, rec_hora: v }))}
                            c={c}
                            isDark={theme.name === 'dark'}
                            accentColor="#3b82f6"
                            accentBg="rgba(59,130,246,0.15)"
                            chipText={theme.name === 'dark' ? '#93c5fd' : '#1d4ed8'}
                            borderColor={c.border}
                            align="right"
                            zIndex={1700}
                          />
                        </div>
                        <div style={{ flex: 1.5 }}>
                          <label style={lbl}>Descripción</label>
                          <input value={form.rec_desc}
                            onChange={e => setForm(p => ({ ...p, rec_desc: e.target.value }))}
                            placeholder="Recontactar con propietario" style={inp} />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Formulario: Mandar a Eliminados ── */}
              {opcion === 'eliminado' && (
                <div style={{ marginBottom: 4 }}>
                  <label style={lbl}>Motivo <span style={{ color: '#ef4444' }}>*</span></label>
                  <textarea value={form.motivo}
                    onChange={e => setForm(p => ({ ...p, motivo: e.target.value }))}
                    placeholder="¿Por qué se descarta definitivamente? (ya vendió, no era el propietario, datos incorrectos, etc.)"
                    rows={3} style={{ ...inp, resize: 'none', borderColor: 'rgba(239,68,68,0.4)' }} />
                </div>
              )}

              {error && (
                <div style={{ margin: '8px 0 12px', padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 12 }}>
                  {error}
                </div>
              )}

              {/* Botones confirmar */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button onClick={onClose} style={{
                  padding: '9px 20px', borderRadius: 9, border: `1.5px solid ${theme.name === 'dark' ? c.border : '#a8a8a0'}`,
                  background: theme.name === 'dark' ? c.bgInput : '#ffffff', color: c.textPrimary,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}>Cancelar</button>
                <button onClick={confirmar} disabled={guardando} style={{
                  padding: '9px 22px', borderRadius: 9, border: 'none',
                  background: activeColor.btn, color: '#fff',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  opacity: guardando ? 0.7 : 1, transition: 'opacity 0.15s',
                }}>
                  {guardando ? 'Guardando…'
                    : opcion === 'fria'      ? '❄️ Mandar a Frías'
                    : opcion === 'eliminado' ? '🗑️ Mandar a Eliminados'
                    : opcion === 'cita'      ? '📅 Confirmar captación'
                    : '✓ Registrar y avanzar'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
